import React from 'react';
import MediaQuery from 'react-responsive';
import * as R from 'ramda';
import Sticky from 'react-stickynode';
import { compose, branch, renderComponent, withState } from 'recompose';
import styled from '@emotion/styled';
import cn from 'classnames';

import UserNav from '../user-nav';
import CompanyBanner from '../company-banner';
import ContextSwitcher from '../context-switcher';
import CustomHeader from '../custom-header';
import MobileHeader from '../mobile-header';
import MobileIconMenu from '../mobile-icon-menu';
import AnonNav from '../anon-nav';
import { Placeholder, UnauthPlaceholder } from '../placeholder';
import { isMobileApp, withLoadedEvent } from '../../common/utils';

const AnonHeader = ({ enabledFeatures, customBranding, publicTabs, allowOpenSignUp }) => customBranding ? (
  <React.Fragment>
    <AnonNav publicTabs={publicTabs} allowOpenSignUp={allowOpenSignUp} />
    <CompanyBanner enabledFeatures={enabledFeatures} customBranding={customBranding} />
  </React.Fragment>
) : null;

const HeaderContainer = styled('header')`
  margin: 0;
  width: 100%;
  position: relative;
  background-color: ${props => props.theme.colors.white};
`;

const StickyHeader = styled(props => (
  <Sticky innerZ={1000}
    enabled={props.isStickyEnabled}
    className={cn(props.className, 'c-header-nav-container-mobile')}>
    <MobileHeader profile={props.profile}
      customBranding={props.customBranding}
      enabledFeatures={props.enabledFeatures} />
    <MobileIconMenu
      features={props.features.mobile_menu}
      hubSwitcher={props.hubSwitcher}
      unseenCounts={props.unseenCounts}
      setStickyEnabled={props.setStickyEnabled}
      unseenNotificationsCount={props.unseenNotificationsCount}
      t={props.i18n.t} />
  </Sticky>
))`
  @media (min-width: 769px) {
    display: none;
  }
`;

const UserHeader = compose(
  withState('isStickyEnabled', 'setStickyEnabled', true)
)(({ customBranding, profile, enabledFeatures, features,
  hubSwitcher, unseenCounts, i18n, moment, unseenNotificationsCount, ...rest
}) => (
  <React.Fragment>
    {profile.attributes.type === 'Corporate' && <ContextSwitcher />}
    <CustomHeader enabledFeatures={enabledFeatures} customBranding={customBranding} />
    <MediaQuery minWidth={769}>
      <HeaderContainer className='c-header-nav-container'>
        <UserNav
          profile={profile}
          enabledFeatures={enabledFeatures}
          features={features.advocate_menu}
          hubSwitcher={hubSwitcher}
          unseenCounts={unseenCounts}
          unseenNotificationsCount={unseenNotificationsCount}
          moment={moment}
          enabledFeatures={enabledFeatures}/>
      </HeaderContainer>
      <CompanyBanner profile={profile} enabledFeatures={enabledFeatures} customBranding={customBranding} />
    </MediaQuery>
    <MediaQuery maxWidth={768}>
      {isMobileApp()
        ? <div className="c-header-nav-container-mobile">
          <MobileHeader profile={profile} customBranding={customBranding} enabledFeatures={enabledFeatures} />
          <MobileIconMenu
            features={features.mobile_menu}
            hubSwitcher={hubSwitcher}
            unseenCounts={unseenCounts}
            unseenNotificationsCount={unseenNotificationsCount}
            t={i18n.t} />
        </div>
        : <StickyHeader
          profile={profile}
          customBranding={customBranding}
          enabledFeatures={enabledFeatures}
          features={features}
          hubSwitcher={hubSwitcher}
          unseenCounts={unseenCounts}
          unseenNotificationsCount={unseenNotificationsCount}
          i18n={i18n}
          {...rest} />}
    </MediaQuery>
  </React.Fragment>
));

export default compose(
  branch(R.propSatisfies(R.isNil, 'isAuth'), renderComponent(UnauthPlaceholder)),
  branch(R.propEq('isAuth', false), renderComponent(withLoadedEvent(AnonHeader))),
  branch(({ profile, features, hubSwitcher, enabledFeatures }) =>
    !(profile && features && hubSwitcher && enabledFeatures), renderComponent(Placeholder)
  ),
  withLoadedEvent
)(UserHeader);
